import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CollectorDatasetsService } from '../../../services/collector-datasets.service';
import { Subscription } from 'rxjs';
import { DatasetFile, ValidationStatus } from '../../../services/collector-datasets.service';
import { CollectorDatasetDetailDialogComponent } from './collector-dataset-detail-dialog.component';

@Component({
  selector: 'app-list-collector-datasets',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatDialogModule, RouterModule],
  templateUrl: './list-collector-datasets.component.html',
  styleUrl: './list-collector-datasets.component.scss'
})
export class ListCollectorDatasetsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() schemaIdInput?: string;
  @Input() schemaNameInput?: string;

  schemaId = '';
  schemaName = '';
  datasets: DatasetFile[] | null = null;
  searchText = '';
  statusFilter: 'all' | 'active' | 'completed' | 'failed' = 'all';

  // Paginación de la vista compacta embebida ("Cargas anteriores").
  embeddedPage = 0;
  readonly embeddedPageSize = 4;
  private datasetsSub?: Subscription;

  get isEmbedded(): boolean {
    return !!this.schemaIdInput;
  }

  constructor(
    private collectorDatasetsService: CollectorDatasetsService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.resolveContext();
    this.reloadDatasets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schemaIdInput'] || changes['schemaNameInput']) {
      this.resolveContext();
      this.reloadDatasets();
    }
  }

  ngOnDestroy(): void {
    this.datasetsSub?.unsubscribe();
  }

  refresh(): void {
    this.reloadDatasets();
  }

  pagedDatasets(): DatasetFile[] {
    const list = this.datasets ?? [];
    const start = this.embeddedPage * this.embeddedPageSize;
    return list.slice(start, start + this.embeddedPageSize);
  }

  embeddedTotalPages(): number {
    return Math.ceil((this.datasets?.length ?? 0) / this.embeddedPageSize);
  }

  embeddedHasPrev(): boolean {
    return this.embeddedPage > 0;
  }

  embeddedHasNext(): boolean {
    return this.embeddedPage < this.embeddedTotalPages() - 1;
  }

  embeddedPrevPage(): void {
    if (this.embeddedHasPrev()) this.embeddedPage--;
  }

  embeddedNextPage(): void {
    if (this.embeddedHasNext()) this.embeddedPage++;
  }

  embeddedPageLabel(): string {
    return `${this.embeddedPage + 1} / ${this.embeddedTotalPages()}`;
  }

  private clampEmbeddedPage(): void {
    const lastPage = Math.max(0, this.embeddedTotalPages() - 1);
    if (this.embeddedPage > lastPage) this.embeddedPage = lastPage;
  }

  setStatusFilter(status: 'all' | 'active' | 'completed' | 'failed'): void {
    this.statusFilter = status;
  }

  filteredDatasets(datasets: DatasetFile[]): DatasetFile[] {
    const term = this.searchText.trim().toLowerCase();

    return datasets.filter((dataset) => {
      const matchesStatus = this.statusFilter === 'all'
        || (this.statusFilter === 'active' && this.isActiveStatus(dataset.validation_status))
        || (this.statusFilter === 'completed' && this.isSuccessStatus(dataset.validation_status))
        || (this.statusFilter === 'failed' && this.isFailedStatus(dataset.validation_status));

      if (!matchesStatus) return false;
      if (!term) return true;

      return [
        dataset.name,
        dataset.schema,
        dataset.schema_id,
        dataset.created_by,
        dataset.created_by_name,
        dataset.created_by_email
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }

  countByStatus(
    datasets: DatasetFile[],
    status: 'active' | 'completed' | 'failed'
  ): number {
    if (status === 'active') {
      return datasets.filter((dataset) => this.isActiveStatus(dataset.validation_status)).length;
    }
    if (status === 'completed') {
      return datasets.filter((dataset) => this.isSuccessStatus(dataset.validation_status)).length;
    }
    return datasets.filter((dataset) => this.isFailedStatus(dataset.validation_status)).length;
  }

  getSchemaDisplay(dataset: DatasetFile): string {
    return dataset.schema || dataset.schema_id || 'Sin modelo';
  }

  operationLabel(operation?: DatasetFile['type']): string {
    if (operation === 'full_replace') return 'Reemplazar todo';
    if (operation === 'replace_scope') return 'Actualizar por filtro';
    if (operation === 'insert') return 'Agregar datos';
    return 'Sin especificar';
  }

  operationIcon(operation?: DatasetFile['type']): string {
    if (operation === 'full_replace') return 'published_with_changes';
    if (operation === 'replace_scope') return 'filter_alt';
    return 'add_circle_outline';
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = 'all';
  }

  openDetail(dataset: DatasetFile): void {
    this.dialog.open(CollectorDatasetDetailDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: dataset
    });
  }

  private resolveContext(): void {
    this.schemaId = (
      this.schemaIdInput ?? this.route.snapshot.queryParamMap.get('schema_id') ?? ''
    ).trim();
    this.schemaName =
      this.schemaNameInput ?? this.route.snapshot.queryParamMap.get('schema_name') ?? '';
  }

  private reloadDatasets(): void {
    const source$ = this.schemaId
      ? this.collectorDatasetsService.watchDatasetsBySchema$(this.schemaId)
      : this.collectorDatasetsService.getDatasetsByCurrentUser$();

    this.datasets = null;
    this.datasetsSub?.unsubscribe();
    this.datasetsSub = source$.subscribe((list) => {
      this.datasets = list ?? [];
      this.clampEmbeddedPage();
    });
  }

  private isFailedStatus(status?: ValidationStatus): boolean {
    return status === 'failed' || !!status?.startsWith('failed_');
  }

  private isSuccessStatus(status?: ValidationStatus): boolean {
    return status === 'validated_metadata' || status === 'completed';
  }

  private isActiveStatus(status?: ValidationStatus): boolean {
    return status === 'uploading'
      || status === 'pending'
      || status === 'queued'
      || status === 'processing'
      || status === 'validating';
  }

  getStatusIcon(status?: ValidationStatus): string {
    if (this.isSuccessStatus(status)) return 'check_circle';
    if (this.isFailedStatus(status)) return 'cancel';
    if (status === 'processing' || status === 'validating') return 'sync';
    if (status === 'queued' || status === 'uploading') return 'schedule';
    return 'hourglass_top';
  }

  getStatusClass(status?: ValidationStatus): string {
    if (this.isSuccessStatus(status)) return 'status-success';
    if (this.isFailedStatus(status)) return 'status-error';
    if (status === 'processing' || status === 'validating') return 'status-processing';
    if (status === 'queued' || status === 'uploading') return 'status-queued';
    return 'status-pending';
  }

  getStatusLabel(status?: ValidationStatus): string {
    if (this.isSuccessStatus(status)) return 'Completado';
    if (status === 'failed_fields')     return 'Error de configuración';
    if (status === 'failed_storage')    return 'Error de archivo';
    if (status === 'failed_validation') return 'Error de validación';
    if (status === 'failed_loading')    return 'Error al cargar';
    if (status === 'failed')            return 'Error';
    if (status === 'validating') return 'Validando';
    if (status === 'processing') return 'Procesando';
    if (status === 'queued')     return 'En cola';
    if (status === 'uploading')  return 'Subiendo';
    return 'Recibido';
  }

  getCreatedBy(dataset: DatasetFile): string {
    return dataset.created_by_email || dataset.created_by || dataset.created_by_name || dataset.created_by_uid || '-';
  }

  formatCreatedAt(createdAt: unknown): string {
    let date: Date | null = null;
    if (createdAt && typeof (createdAt as any).seconds === 'number') {
      date = new Date((createdAt as any).seconds * 1000);
    } else if (createdAt instanceof Date) {
      date = createdAt;
    } else if (typeof createdAt === 'string' || typeof createdAt === 'number') {
      const parsed = new Date(createdAt);
      date = Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (!date) return '';
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
